import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Loader2, Search, ChevronLeft, ChevronRight, ArrowLeft, Phone, Mail,
  MapPin, FileText, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import ServiceOptionChip, { SERVICE_OPTIONS, SERVICE_OPTION_THEMES } from '@/components/ServiceOptionChip';

const STAGE_ACTIONS = {
  1: [
    { label: '+ Book appraisal', route: '/appraisals' },
    { label: 'Telemarketing', route: '/telemarketing' },
    { label: 'Calendar', route: '/calendar' },
  ],
  2: [
    { label: 'Material requirements', route: '/material-requirements' },
    { label: 'Job tracking', route: '/job-tracking' },
  ],
  3: [
    { label: 'Job tracking', route: '/job-tracking' },
    { label: 'Health checks', route: '/health-check' },
  ],
  4: [
    { label: 'Invoices', route: '/invoices' },
    { label: 'Contractor pay', route: '/contractors' },
  ],
  5: [
    { label: 'Telemarketing', route: '/telemarketing' },
    { label: 'Maintenance schedule', route: '/calendar' },
  ],
};

const STAGE_LABELS = {
  1: 'Sales & Quoting',
  2: 'Accepted / Pre-Plan',
  3: 'Work Booked / Active',
  4: 'Completion & Invoice',
  5: 'Maintenance / Future',
};

export default function StageDetailPage() {
  const { stageId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sid = parseInt(stageId, 10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Seed serviceFilter from the URL on first mount so deep-links work.
  const [serviceFilter, setServiceFilter] = useState(searchParams.get('service_option') || '');
  const [advancingId, setAdvancingId] = useState(null);

  // Keep the URL query string in sync with the filter so links can be
  // shared / bookmarked. Skips push when nothing changes to avoid a
  // history-loop on every render.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (serviceFilter) next.set('service_option', serviceFilter);
    else next.delete('service_option');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFilter]);

  // Canonical next-status map — mirrored from backend STATUS_FLOW so the
  // button label is correct without a per-row round-trip.
  const NEXT_STATUS = {
    'Client Data':         'Appraisal Booked',
    'Appraisal Booked':    'Quote Sent',
    'Quote Sent':          'Accepted',
    'Accepted':            'Work Booked',
    'Work Booked':         'Work In Progress',
    'Work In Progress':    'Completed',
    'Completed':           'Invoiced',
    'Invoiced':            'Paid',
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 25, search });
      if (serviceFilter) qs.set('service_option', serviceFilter);
      const res = await api.get(`/pipeline/stage/${sid}?${qs.toString()}`);
      setData(res.data);
    } catch { setData({ items: [], total: 0, pages: 1, page: 1 }); }
    setLoading(false);
  }, [api, sid, page, search, serviceFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAdvance = async (a, e) => {
    e.stopPropagation();
    const next = NEXT_STATUS[a.appraisal_status];
    if (!next) return;
    if (!window.confirm(`Move #${a.appraisal_no || a._id.slice(-6)} from "${a.appraisal_status}" → "${next}"?`)) return;
    setAdvancingId(a._id);
    try {
      const res = await api.post(`/pipeline/advance/${a._id}`);
      toast.success(`Advanced to "${res.data.new_status}"${res.data.new_stage !== res.data.previous_stage ? ` · now in Stage ${res.data.new_stage}` : ''}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to advance — try again.');
    }
    setAdvancingId(null);
  };

  const stageLabel = STAGE_LABELS[sid] || 'Stage';
  const actions = STAGE_ACTIONS[sid] || [];

  return (
    <div data-testid="stage-detail-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => navigate('/pipeline')} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-1">
            <ArrowLeft size={12} /> Back to Pipeline Hub
          </button>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">
            Stage {sid} · {stageLabel}
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            <span className="font-mono-num font-bold text-slate-700">{data.total.toLocaleString()}</span> {data.total === 1 ? 'job' : 'jobs'} in this stage · statuses: {(data.statuses || []).join(' · ')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {actions.map(a => (
            <Link
              key={a.route}
              data-testid={`stage-action-${a.label.replace(/\s+/g, '-').toLowerCase()}`}
              to={a.route}
              className="text-xs px-3 py-1.5 border border-[#D35400] text-[#D35400] rounded-md hover:bg-[#D35400] hover:text-white font-semibold transition-all"
            >
              {a.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="stage-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by appraisal #, comments…"
            className="w-full pl-10 pr-4 py-2 border border-[#E2E2DF] rounded-md text-sm focus:border-[#D35400] focus:outline-none"
          />
        </div>
        {/* Service-option filter pills. Click a pill to filter the
            worklist to that workflow; click again (or "All") to clear. */}
        <div className="flex items-center gap-1.5" data-testid="stage-service-filter">
          <button
            data-testid="stage-service-filter-all"
            onClick={() => { setServiceFilter(''); setPage(1); }}
            className={`text-[11px] px-2.5 py-1.5 rounded-md font-semibold border transition-all ${serviceFilter === '' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
          >
            All
          </button>
          {SERVICE_OPTIONS.map(opt => {
            const t = SERVICE_OPTION_THEMES[opt];
            const active = serviceFilter === opt;
            return (
              <button
                key={opt}
                data-testid={`stage-service-filter-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => { setServiceFilter(active ? '' : opt); setPage(1); }}
                className={`text-[11px] px-2.5 py-1.5 rounded-md font-semibold border transition-all inline-flex items-center gap-1.5 ${active ? `${t.bg} ${t.text} ${t.ring} ring-2 border-transparent` : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          <FileText size={28} className="mx-auto mb-2 opacity-40" />
          <p>No jobs in this stage{search ? ' matching your search' : ''}.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Appraisal</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Property</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Service</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Value</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Date</th>
                <th className="text-right px-3 py-2">Advance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map(a => {
                const next = NEXT_STATUS[a.appraisal_status];
                const busy = advancingId === a._id;
                return (
                <tr key={a._id} data-testid={`stage-row-${a._id}`} onClick={() => navigate(`/appraisals/${a._id}`)} className="hover:bg-amber-50/30 cursor-pointer">
                  <td className="px-3 py-2 font-mono-num font-bold text-slate-800">#{a.appraisal_no || a.access_id || a._id.slice(-6)}</td>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800 truncate max-w-[180px]">{a.client_name || '—'}</p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                      {a.client_phone && <span className="flex items-center gap-1"><Phone size={9} />{a.client_phone}</span>}
                      {a.client_email && <span className="flex items-center gap-1 truncate max-w-[140px]"><Mail size={9} />{a.client_email}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-slate-600 truncate max-w-[260px]">
                    {a.property_address ? <span className="flex items-center gap-1"><MapPin size={10} className="text-slate-400 flex-shrink-0" />{a.property_address}</span> : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold ring-1 ring-slate-200 whitespace-nowrap">
                      {a.appraisal_status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <ServiceOptionChip value={a.service_option || 'Reroof'} />
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num hidden md:table-cell">
                    {a.total_quote_inc_gst ? `$${a.total_quote_inc_gst.toLocaleString()}` : (a.calc_customer_total ? `$${a.calc_customer_total.toLocaleString()}` : '—')}
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell text-slate-500">{a.appraisal_date || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {next ? (
                      <button
                        data-testid={`advance-stage-btn-${a._id}`}
                        disabled={busy}
                        onClick={(e) => handleAdvance(a, e)}
                        title={`Move to "${next}"`}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-600 hover:text-white font-bold uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-wait whitespace-nowrap"
                      >
                        {busy ? <Loader2 size={10} className="animate-spin" /> : <ArrowRight size={10} />}
                        {next}
                      </button>
                    ) : (
                      <span data-testid={`advance-stage-terminal-${a._id}`} className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Final</span>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-slate-400">Page {data.page} of {data.pages}</span>
          <div className="flex gap-1">
            <button data-testid="stage-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button data-testid="stage-next" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
